import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RiArrowGoBackLine, RiDeleteBin6Line, RiEyeLine, RiCheckLine, RiCloseLine, RiArrowRightLine } from 'react-icons/ri';
import { judge, ensureFont } from './recognise';
import './DrawMode.css';

/* ══════════════════════════════════════════════════════════════
   KANA DRAW  —  handwriting self-check

   You are shown the romaji and draw the character from memory, and the
   app marks it — see recognise.js for how, and for the accuracy it was
   measured at. Every drawing gets a verdict; an earlier version abstained
   when it was unsure, which in practice meant abstaining almost always
   and handing the decision back to the learner, defeating the point. The
   override remains, because the grader is good rather than perfect and
   the learner is the one looking at both shapes.

   Strokes are kept as arrays of points rather than being painted
   straight onto the canvas. That is what makes undo possible at all,
   and it means the pad can be redrawn at a new size — on rotate, or
   when the fullscreen toggle changes the layout — without losing what
   you drew.

   ── REMOVING THIS FEATURE ─────────────────────────────────────
   Delete this folder, then delete the six blocks in
   pages/app/Kana.jsx tagged `kana-draw`:

     1. the import of this file
     2. the MODES entry
     3. EMPTY_DRAW_STATE and its useState
     4. the reset line in the chart-mode effect
     5. the session-building effect
     6. the render block

   Nothing else references it. There are no backend changes, no new
   API calls, and no new collections — grading goes through the same
   onGrade handler the other modes use, so any progress it recorded is
   ordinary kana mastery and stays valid.
══════════════════════════════════════════════════════════════ */

const INK = '#f7f3ea';
const GHOST = 'rgba(150, 199, 222, 0.21)';   // a cool guide tint, not warm ink
const LINE_W = 7;

/* One point per pointer sample would give visibly straight segments on a
   fast stroke; quadratic joins through the midpoints smooth it without
   needing to store anything extra. */
function strokePath(ctx, points) {
  if (points.length < 2) {
    if (points.length === 1) {
      ctx.beginPath();
      ctx.arc(points[0].x, points[0].y, LINE_W / 2, 0, Math.PI * 2);
      ctx.fillStyle = INK;
      ctx.fill();
    }
    return;
  }
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length - 1; i += 1) {
    const mid = { x: (points[i].x + points[i + 1].x) / 2, y: (points[i].y + points[i + 1].y) / 2 };
    ctx.quadraticCurveTo(points[i].x, points[i].y, mid.x, mid.y);
  }
  ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
  ctx.stroke();
}

export default function DrawMode({
  script,
  allData = [],
  onGrade,
  drawState,
  setDrawState,
  fullscreen = false,
  keyboardEnabled = true,
}) {
  const { cards, index, revealed, verdict, correct, done } = drawState;
  const card = cards[index];

  const padRef = useRef(null);
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);        // committed strokes, in CSS pixels
  const currentRef = useRef(null);      // the stroke being drawn
  const sizeRef = useRef(0);
  const [strokeCount, setStrokeCount] = useState(0);
  /* On by default. Recall is the point of the exercise, but a blank pad
     is a poor place to start one — the guide tells you where the
     character sits and how large it should be, and you can switch it off
     the moment you would rather be tested. */
  const [trace, setTrace] = useState(true);

  /* Templates are rasterised from Noto Sans JP. Asking for it before the
     first judgement matters: rasterise too early and every template is
     built in the fallback font, so every comparison is made against the
     wrong shapes. */
  useEffect(() => { ensureFont(); }, []);

  /* Repaint everything. Cheap enough at this size to do on every pointer
     move, and it keeps one function responsible for what the pad looks
     like rather than scattering draw calls around. */
  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    const size = sizeRef.current;
    if (!canvas || !size) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, size, size);

    // Practice-paper guides: a box inset from the edge, halved both ways.
    const pad = size * 0.08;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.07)';
    ctx.lineWidth = 1;
    ctx.strokeRect(pad, pad, size - pad * 2, size - pad * 2);
    ctx.setLineDash([5, 7]);
    ctx.beginPath();
    ctx.moveTo(size / 2, pad); ctx.lineTo(size / 2, size - pad);
    ctx.moveTo(pad, size / 2); ctx.lineTo(size - pad, size / 2);
    ctx.stroke();
    ctx.restore();

    // The character itself, when tracing or after the reveal.
    if ((trace || revealed) && card) {
      ctx.save();
      ctx.fillStyle = GHOST;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${Math.round(size * 0.62)}px "Noto Sans JP", sans-serif`;
      ctx.fillText(card.kana, size / 2, size / 2 + size * 0.02);
      ctx.restore();
    }

    ctx.save();
    ctx.strokeStyle = INK;
    ctx.lineWidth = LINE_W;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    [...strokesRef.current, currentRef.current].forEach(pts => {
      if (pts && pts.length) strokePath(ctx, pts);
    });
    ctx.restore();
  }, [trace, revealed, card]);

  /* Match the backing store to the CSS box and the device pixel ratio, or
     the ink is blurry on any screen that is not exactly 1x. Re-runs on
     resize, which is also what makes the fullscreen toggle safe. */
  useLayoutEffect(() => {
    const pad = padRef.current;
    const canvas = canvasRef.current;
    if (!pad || !canvas) return undefined;

    const fit = () => {
      const size = Math.round(pad.clientWidth);
      if (!size) return;
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = size;
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      canvas.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
      repaint();
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(pad);
    return () => ro.disconnect();
  }, [repaint]);

  useEffect(() => { repaint(); }, [repaint]);

  /* A new card is a blank pad. */
  useEffect(() => {
    strokesRef.current = [];
    currentRef.current = null;
    setStrokeCount(0);
    repaint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, script]);

  const pointAt = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    if (revealed) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    currentRef.current = [pointAt(e)];
    repaint();
  };

  const onPointerMove = (e) => {
    if (!currentRef.current) return;
    currentRef.current.push(pointAt(e));
    repaint();
  };

  const endStroke = () => {
    if (!currentRef.current) return;
    if (currentRef.current.length) strokesRef.current.push(currentRef.current);
    currentRef.current = null;
    setStrokeCount(strokesRef.current.length);
    repaint();
  };

  const undo = () => {
    strokesRef.current.pop();
    setStrokeCount(strokesRef.current.length);
    repaint();
  };

  const clear = () => {
    strokesRef.current = [];
    currentRef.current = null;
    setStrokeCount(0);
    repaint();
  };

  const check = () => {
    const result = judge(strokesRef.current, sizeRef.current, LINE_W, card?.kana, allData);
    setDrawState(s => ({ ...s, revealed: true, verdict: result }));
  };

  const grade = (got) => {
    if (card) onGrade?.(card, got);
    setDrawState(s => {
      const last = s.index >= s.cards.length - 1;
      return {
        ...s,
        index: last ? s.index : s.index + 1,
        revealed: false,
        verdict: null,
        correct: s.correct + (got ? 1 : 0),
        done: last,
      };
    });
  };

  const restart = () => {
    strokesRef.current = [];
    setStrokeCount(0);
    setDrawState(s => ({ ...s, index: 0, revealed: false, verdict: null, correct: 0, done: false }));
  };

  /* Enter reveals, then Enter again counts it right — the same rhythm as
     the writing mode, so the muscle memory carries over. */
  useEffect(() => {
    if (!keyboardEnabled || done) return undefined;
    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); if (revealed) grade(verdict ? verdict.correct : true); else check(); }
      if (e.key === 'Backspace') { e.preventDefault(); undo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardEnabled, revealed, done, index, verdict]);

  if (!cards.length) return null;

  if (done) {
    const pct = Math.round((correct / cards.length) * 100);
    return (
      <div className={`study-stage${fullscreen ? ' fullscreen' : ''}`} style={{ '--stage-w': '460px' }}>
        <div className="study-stage-body">
          <div className="kdraw-done">
            <div className="kdraw-done-score" style={{ color: pct >= 60 ? '#4ade80' : '#f97316' }}>{pct}%</div>
            <h3>Drawing session done</h3>
            <p>You recalled {correct} of {cards.length} characters.</p>
            <button className="btn-primary" onClick={restart}>Draw them again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    /* The same shell every other mode uses: a centred stage with the
       actions in the fixed footer bar. This was a `ks-card` before, which
       gave Draw a panel none of its siblings has and made it look like a
       widget bolted onto the page rather than one of the modes. */
    <div className={`study-stage${fullscreen ? ' fullscreen' : ''}`} style={{ '--stage-w': '460px' }}>
      <div className="study-stage-body kdraw">
        <div className="kdraw-head">
          <span className="kdraw-count">{index + 1} <i>/ {cards.length}</i></span>
          <button
            type="button"
            className={`kdraw-trace${trace ? ' on' : ''}`}
            onClick={() => setTrace(t => !t)}
            title="Show the character faintly so you can trace it"
          >
            <RiEyeLine size={13} /> Trace
          </button>
        </div>

        <div className="kdraw-prompt">
          <span className="kdraw-prompt-kicker">Draw this character</span>
          <span className="kdraw-prompt-romaji">{card?.romaji}</span>
        </div>

        <div ref={padRef} className={`kdraw-pad${revealed ? ` revealed ${verdict?.correct ? 'right' : 'wrong'}` : ''}`}>
          <canvas
            ref={canvasRef}
            className="kdraw-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
            onPointerLeave={endStroke}
          />
          {strokeCount === 0 && !currentRef.current && !revealed && !trace && (
            <span className="kdraw-hint">Draw here</span>
          )}
        </div>

        <div className="kdraw-tools">
          <button type="button" onClick={undo} disabled={!strokeCount || revealed}>
            <RiArrowGoBackLine size={14} /> Undo
          </button>
          <button type="button" onClick={clear} disabled={!strokeCount || revealed}>
            <RiDeleteBin6Line size={14} /> Clear
          </button>
        </div>
      </div>

      {/* Verdict and actions in the footer, exactly as Quiz and Writing
          report theirs — same bar, same colours, same place to look. */}
      <div className={`study-footer${revealed ? (verdict?.correct ? ' correct' : ' wrong') : ''}`}>
        <div className="study-footer-inner">
          {revealed ? (
            <div className="study-feedback">
              <strong>
                {verdict?.correct
                  ? <><RiCheckLine size={15} /> Correct</>
                  : <><RiCloseLine size={15} /> Not quite</>}
              </strong>
              <span>
                {card?.kana} = {card?.romaji}
                {!verdict?.correct && verdict?.confusedWith
                  ? `  ·  yours reads as ${verdict.confusedWith.kana}`
                  : ''}
              </span>
            </div>
          ) : (
            <span className="study-footer-hint">Draw it, then check</span>
          )}

          {!revealed ? (
            <button className="btn-primary" onClick={check} disabled={!strokeCount}>Check</button>
          ) : (
            <>
              {/* The override keeps its place beside Next rather than
                  taking the primary slot -- it is the exception. */}
              <button className="btn-ghost kdraw-override" onClick={() => grade(!verdict?.correct)}>
                {verdict?.correct ? 'Mark wrong' : 'I had it right'}
              </button>
              <button className="btn-primary" onClick={() => grade(!!verdict?.correct)}>
                Next <RiArrowRightLine size={15} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
